import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createAppointmentThunk,
  searchPatientsThunk,
} from "../../../redux/Thunk/Appointment/AppointmentThunks";
import { setPatientSearchResults } from "../../../redux/Reducer/Appointment/AppointmentSlice";
import Close from "../../common/icons/Close";
import CustomSelect from "../../common/CustomSelect";

const NewAppointmentModal = ({ isOpen, onClose, initialDate, initialTimeSlot }) => {
  const dispatch = useDispatch();
  const patientSearchResults = useSelector(
    (state) => state.appointment.patientSearchResults
  );
  const doctorSchedule = useSelector(
    (state) => state.appointment.doctorSchedule
  );

  const defaultNewFee = doctorSchedule?.defaultNewFee || 500;
  const defaultFollowupFee = doctorSchedule?.defaultFollowupFee || 300;

  const [formData, setFormData] = useState({
    patientName: "",
    patientSurname: "",
    patientPhone: "",
    patientAge: "",
    patientDob: "",
    patientGender: "Male",
    patientPincode: "",
    patientCity: "",
    appointmentDate: initialDate || new Date().toISOString().split("T")[0],
    slotStartTime: initialTimeSlot || "09:00",
    slotEndTime: "09:15",
    visitType: "NEW",
    consultationMode: "IN_PERSON",
    consultationFee: defaultNewFee,
    paymentStatus: "UNPAID",
    paymentMethod: "CASH",
    notes: "",
    PID: "",
  });

  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [ageMode, setAgeMode] = useState("AGE");
  const [locationMode, setLocationMode] = useState("CITY");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);

  const calculateAgeFromDob = (dobString) => {
    if (!dobString) return "";
    const dob = new Date(dobString);
    if (isNaN(dob.getTime())) return "";
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age >= 0 ? String(age) : "";
  };

  // Helper to prevent numeric digits in text fields (First Name, Surname, City)
  const sanitizeLettersOnly = (val) => {
    if (!val) return "";
    return val.replace(/[0-9]/g, "");
  };

  useEffect(() => {
    if (initialDate) setFormData((prev) => ({ ...prev, appointmentDate: initialDate }));
    if (initialTimeSlot) setFormData((prev) => ({ ...prev, slotStartTime: initialTimeSlot }));
  }, [initialDate, initialTimeSlot]);

  // Adjust fee default based on Visit Type selection
  const handleVisitTypeChange = (type) => {
    const fee = type === "FOLLOW_UP" ? defaultFollowupFee : defaultNewFee;
    setFormData((prev) => ({
      ...prev,
      visitType: type,
      consultationFee: fee,
    }));
  };

  // Clear patient selection and erase patient details
  const handleClearSelectedPatient = () => {
    setSelectedPatient(null);
    setSearchQuery("");
    setFormData((prev) => ({
      ...prev,
      patientName: "",
      patientSurname: "",
      patientPhone: "",
      patientAge: "",
      patientDob: "",
      patientPincode: "",
      patientCity: "",
      PID: "",
    }));
    dispatch(setPatientSearchResults([]));
    setShowSearchDropdown(false);
  };

  // Search patients on typing phone or name - clears previous patient details on edit
  const handlePatientSearchChange = (query) => {
    if (selectedPatient) {
      setSelectedPatient(null);
      setFormData((prev) => ({
        ...prev,
        patientName: "",
        patientSurname: "",
        patientAge: "",
        patientDob: "",
        patientPincode: "",
        patientCity: "",
        PID: "",
      }));
    }

    setSearchQuery(query);
    const extractedDigits = query.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({
      ...prev,
      patientPhone: extractedDigits,
    }));

    if (query.trim().length > 1) {
      dispatch(searchPatientsThunk(query));
      setShowSearchDropdown(true);
    } else {
      dispatch(setPatientSearchResults([]));
      setShowSearchDropdown(false);
    }
  };

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    const computedAge = patient.age || calculateAgeFromDob(patient.dateofbirth) || "";
    if (patient.dateofbirth && !patient.age) {
      setAgeMode("DOB");
    } else {
      setAgeMode("AGE");
    }
    if (patient.pincode && !patient.city) {
      setLocationMode("PINCODE");
    } else {
      setLocationMode("CITY");
    }
    setSearchQuery(`${patient.name} (${patient.phoneNumber})`);
    setFormData((prev) => ({
      ...prev,
      patientName: sanitizeLettersOnly(patient.name) || "",
      patientSurname: sanitizeLettersOnly(patient.surname) || "",
      patientPhone: patient.phoneNumber || "",
      patientAge: computedAge,
      patientDob: patient.dateofbirth || "",
      patientGender: patient.gender || prev.patientGender,
      patientPincode: patient.pincode || "",
      patientCity: sanitizeLettersOnly(patient.city) || "",
      PID: patient.PID,
    }));
    setShowSearchDropdown(false);
  };

  // Comprehensive 11-Field Validation with Strict 10-digit Phone & No-Number Text Checks
  const isValidPhone = /^[0-9]{10}$/.test(formData.patientPhone?.trim());
  const isValidFirstName = Boolean(formData.patientName?.trim() && !/[0-9]/.test(formData.patientName));
  const isValidSurname = Boolean(formData.patientSurname?.trim() && !/[0-9]/.test(formData.patientSurname));
  const isValidGender = Boolean(formData.patientGender?.trim());
  const isValidAgeOrDob = ageMode === "AGE"
    ? (formData.patientAge !== "" && !isNaN(Number(formData.patientAge)) && Number(formData.patientAge) >= 0)
    : Boolean(formData.patientDob?.trim());
  const isValidLocation = locationMode === "CITY"
    ? Boolean(formData.patientCity?.trim() && !/[0-9]/.test(formData.patientCity))
    : /^[0-9]{6}$/.test(formData.patientPincode?.trim());
  const isValidDate = Boolean(formData.appointmentDate);
  const isValidTimeSlot = Boolean(formData.slotStartTime);
  const isValidVisitType = Boolean(formData.visitType);
  const isValidFee = formData.consultationFee !== "" && formData.consultationFee !== null && !isNaN(Number(formData.consultationFee)) && Number(formData.consultationFee) >= 0;
  const isValidPayStatus = Boolean(formData.paymentStatus);

  // Date Today Indicator Helpers
  const todayStr = new Date().toISOString().split("T")[0];
  const isAppointmentDateToday = formData.appointmentDate === todayStr;
  const isAppointmentDatePast = formData.appointmentDate && formData.appointmentDate < todayStr;

  // 1-Click Set Current Time Now
  const handleSetCurrentTimeNow = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const currentTimeStr = `${hours}:${minutes}`;

    const end = new Date(now.getTime() + 15 * 60 * 1000);
    const endHours = String(end.getHours()).padStart(2, "0");
    const endMinutes = String(end.getMinutes()).padStart(2, "0");
    const endTimeStr = `${endHours}:${endMinutes}`;

    setFormData((prev) => ({
      ...prev,
      slotStartTime: currentTimeStr,
      slotEndTime: endTimeStr,
    }));
  };

  const isFormComplete = Boolean(
    isValidPhone &&
    isValidFirstName &&
    isValidSurname &&
    isValidGender &&
    isValidAgeOrDob &&
    isValidLocation &&
    isValidDate &&
    isValidTimeSlot &&
    isValidVisitType &&
    isValidFee &&
    isValidPayStatus
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormComplete) {
      return;
    }
    await dispatch(createAppointmentThunk(formData));
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white w-full max-w-xl rounded-3xl shadow-2xl overflow-hidden border border-gray-100 animate-fadeIn my-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-5 md:p-6 text-white flex items-center justify-between">
          <div>
            <h2 className="text-lg md:text-xl font-bold">Book New Consultation</h2>
            <p className="text-xs text-primary-100 mt-0.5">
              Setup appointment slot & patient details (Unified Patient System)
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 border border-white/50 shadow-md transition-all active:scale-95 cursor-pointer text-white"
            title="Close Modal"
          >
            <Close fill="#ffffff" width={22} height={22} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-5 md:p-6 space-y-4 max-h-[78vh] overflow-y-auto">
          {/* Patient Quick Search / Auto-fill */}
          <div className="relative">
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5 flex items-center justify-between">
              <span>Patient Phone / Name <span className="text-red-500">*</span></span>
              {selectedPatient && (
                <span className="text-[10px] text-emerald-700 font-extrabold bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                  ✓ Selected Existing Patient
                </span>
              )}
            </label>

            {selectedPatient ? (
              <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-emerald-50/90 to-primary-50/80 border border-emerald-200/90 shadow-2xs">
                <div className="flex items-center gap-2.5">
                  <span className="w-7 h-7 rounded-full bg-emerald-600 text-white font-black text-xs flex items-center justify-center shadow-2xs">
                    ✓
                  </span>
                  <div>
                    <span className="font-bold text-gray-900 text-xs block">
                      {selectedPatient.name} {selectedPatient.surname || ""}
                    </span>
                    <span className="text-[11px] text-gray-600 font-semibold">
                      📞 {selectedPatient.phoneNumber} • PID: {selectedPatient.PID}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleClearSelectedPatient}
                  className="text-xs font-bold text-gray-700 hover:text-red-700 bg-white hover:bg-red-50 border border-gray-200 hover:border-red-200 px-3 py-1.5 rounded-lg transition-all cursor-pointer shadow-2xs active:scale-95 flex items-center gap-1"
                >
                  <span>Change</span>
                  <span className="font-black">✕</span>
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  required
                  placeholder="Type 10-digit Phone or Patient Name to search..."
                  value={searchQuery}
                  onChange={(e) => handlePatientSearchChange(e.target.value)}
                  onFocus={() => {
                    if (searchQuery.trim().length > 1) setShowSearchDropdown(true);
                  }}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-xs font-semibold focus:ring-2 focus:ring-primary-500 outline-none"
                />

                {/* Search Dropdown */}
                {showSearchDropdown && patientSearchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 shadow-xl rounded-2xl z-50 max-h-48 overflow-y-auto divide-y divide-gray-50">
                    {patientSearchResults.map((patient) => (
                      <div
                        key={patient.PID}
                        onClick={() => handleSelectPatient(patient)}
                        className="p-3 hover:bg-primary-50 cursor-pointer flex items-center justify-between text-xs transition-all"
                      >
                        <div>
                          <span className="font-bold text-gray-900">
                            {patient.name} {patient.surname || ""}
                          </span>
                          <span className="text-gray-500 ml-2">({patient.phoneNumber})</span>
                        </div>
                        <span className="text-[10px] font-semibold text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">
                          PID: {patient.PID}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Row 1: First Name - Last Name - Gender */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                placeholder="First Name (Letters only)"
                value={formData.patientName}
                onChange={(e) => setFormData({ ...formData, patientName: sanitizeLettersOnly(e.target.value) })}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold focus:ring-2 focus:ring-primary-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                Surname / Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                placeholder="Surname (Letters only)"
                value={formData.patientSurname}
                onChange={(e) => setFormData({ ...formData, patientSurname: sanitizeLettersOnly(e.target.value) })}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold focus:ring-2 focus:ring-primary-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                Gender <span className="text-red-500">*</span>
              </label>
              <CustomSelect
                value={formData.patientGender}
                onChange={(val) => setFormData({ ...formData, patientGender: val })}
                options={[
                  { value: "Male", label: "Male" },
                  { value: "Female", label: "Female" },
                  { value: "Other", label: "Other" },
                ]}
              />
            </div>
          </div>

          {/* Row 2: Age/DOB - City/Pincode */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider shrink-0">
                  {ageMode === "AGE" ? "Age (Yrs)" : "Date of Birth"} <span className="text-red-500">*</span>
                </label>
                <div className="flex-1 max-w-[130px] flex items-center p-0.5 bg-gray-200/70 rounded-md text-[9px] font-bold ml-auto">
                  <button
                    type="button"
                    onClick={() => setAgeMode("AGE")}
                    className={`flex-1 py-0.5 text-center rounded transition-all cursor-pointer ${ageMode === "AGE" ? "bg-white text-gray-900 shadow-2xs font-extrabold" : "text-gray-500 hover:text-gray-900"}`}
                  >
                    Age
                  </button>
                  <button
                    type="button"
                    onClick={() => setAgeMode("DOB")}
                    className={`flex-1 py-0.5 text-center rounded transition-all cursor-pointer ${ageMode === "DOB" ? "bg-white text-gray-900 shadow-2xs font-extrabold" : "text-gray-500 hover:text-gray-900"}`}
                  >
                    DOB
                  </button>
                </div>
              </div>
              {ageMode === "AGE" ? (
                <input
                  type="number"
                  required
                  placeholder="Yrs"
                  value={formData.patientAge}
                  onChange={(e) => setFormData({ ...formData, patientAge: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold focus:ring-2 focus:ring-primary-500 outline-none"
                />
              ) : (
                <input
                  type="date"
                  required
                  value={formData.patientDob || ""}
                  onChange={(e) => {
                    const dob = e.target.value;
                    const calculatedAge = calculateAgeFromDob(dob);
                    setFormData({ ...formData, patientDob: dob, patientAge: calculatedAge });
                  }}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold focus:ring-2 focus:ring-primary-500 outline-none"
                />
              )}
            </div>

            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider shrink-0">
                  {locationMode === "CITY" ? "City" : "Pincode"} <span className="text-red-500">*</span>
                </label>
                <div className="flex-1 max-w-[130px] flex items-center p-0.5 bg-gray-200/70 rounded-md text-[9px] font-bold ml-auto">
                  <button
                    type="button"
                    onClick={() => setLocationMode("CITY")}
                    className={`flex-1 py-0.5 text-center rounded transition-all cursor-pointer ${locationMode === "CITY" ? "bg-white text-gray-900 shadow-2xs font-extrabold" : "text-gray-500 hover:text-gray-900"}`}
                  >
                    City
                  </button>
                  <button
                    type="button"
                    onClick={() => setLocationMode("PINCODE")}
                    className={`flex-1 py-0.5 text-center rounded transition-all cursor-pointer ${locationMode === "PINCODE" ? "bg-white text-gray-900 shadow-2xs font-extrabold" : "text-gray-500 hover:text-gray-900"}`}
                  >
                    Pin
                  </button>
                </div>
              </div>
              {locationMode === "CITY" ? (
                <input
                  type="text"
                  required
                  placeholder="City Name (Letters only)"
                  value={formData.patientCity}
                  onChange={(e) => setFormData({ ...formData, patientCity: sanitizeLettersOnly(e.target.value) })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold focus:ring-2 focus:ring-primary-500 outline-none"
                />
              ) : (
                <input
                  type="text"
                  required
                  placeholder="6-digit Pincode"
                  value={formData.patientPincode}
                  onChange={(e) => setFormData({ ...formData, patientPincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold focus:ring-2 focus:ring-primary-500 outline-none"
                />
              )}
            </div>
          </div>

          {/* Schedule Date & Time Slot */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-gray-50 p-3.5 rounded-2xl border border-gray-100">
            <div>
              <div className="flex items-center justify-between gap-1 mb-1.5">
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Date <span className="text-red-500">*</span>
                </label>

                {/* Today / Not Today Indicator Pill */}
                {isAppointmentDateToday ? (
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-50 text-emerald-800 border border-emerald-200/80 flex items-center gap-1.5 shadow-2xs">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-2xs shadow-emerald-500/50"></span>
                    <span>Today</span>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, appointmentDate: todayStr })}
                    className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-amber-50 text-amber-900 hover:bg-amber-100 border border-amber-200/80 flex items-center gap-1.5 shadow-2xs transition-all cursor-pointer"
                    title="Click to reset appointment date to Today"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shadow-2xs shadow-amber-500/50 animate-pulse"></span>
                    <span>{isAppointmentDatePast ? "Past Date" : "Future Date"} (Click for Today)</span>
                  </button>
                )}
              </div>
              <input
                type="date"
                required
                value={formData.appointmentDate}
                onChange={(e) => setFormData({ ...formData, appointmentDate: e.target.value })}
                className={`w-full px-3 py-2 rounded-xl border text-xs font-semibold bg-white outline-none transition-all ${
                  isAppointmentDateToday ? "border-gray-200 focus:ring-2 focus:ring-primary-500" : "border-amber-300 focus:ring-2 focus:ring-amber-500"
                }`}
              />
            </div>
            <div>
              <div className="flex items-center justify-between gap-1 mb-1.5">
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Time Slot <span className="text-red-500">*</span>
                </label>

                {/* 1-Click Set Current Time Now */}
                <button
                  type="button"
                  onClick={handleSetCurrentTimeNow}
                  className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-primary-50 text-primary-800 hover:bg-primary-600 hover:text-white border border-primary-200/80 flex items-center gap-1 shadow-2xs transition-all cursor-pointer active:scale-95"
                  title="Click to auto-select current time"
                >
                  <span>⚡ Now</span>
                </button>
              </div>
              <input
                type="time"
                required
                value={formData.slotStartTime}
                onChange={(e) => setFormData({ ...formData, slotStartTime: e.target.value })}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold bg-white outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Visit Type & Fee */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                Visit Type <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                {["NEW", "FOLLOW_UP", "EMERGENCY"].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleVisitTypeChange(type)}
                    className={`flex-1 py-2 text-xs font-bold rounded-xl border transition-all ${
                      formData.visitType === type
                        ? "bg-primary-600 text-white border-primary-600 shadow-xs"
                        : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    {type.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                Consultation Fee (₹) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                required
                value={formData.consultationFee}
                onChange={(e) => setFormData({ ...formData, consultationFee: e.target.value })}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold focus:ring-2 focus:ring-primary-500 outline-none"
              />
            </div>
          </div>

          {/* Payment Status Tag */}
          <div className="flex items-center justify-between bg-emerald-50/50 p-3.5 rounded-2xl border border-emerald-100">
            <div>
              <span className="block text-xs font-bold text-emerald-900 uppercase tracking-wider">
                Fee Collection Status <span className="text-red-500">*</span>
              </span>
              <span className="text-[11px] text-emerald-700">Tag payment collection upon booking</span>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, paymentStatus: "PAID" })}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                  formData.paymentStatus === "PAID"
                    ? "bg-emerald-600 text-white shadow-xs"
                    : "bg-white text-emerald-700 border border-emerald-200"
                }`}
              >
                Paid
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, paymentStatus: "UNPAID" })}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                  formData.paymentStatus === "UNPAID"
                    ? "bg-amber-500 text-white shadow-xs"
                    : "bg-white text-amber-700 border border-amber-200"
                }`}
              >
                Unpaid
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-xs font-semibold text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormComplete}
              className={`px-6 py-2.5 text-xs font-bold rounded-xl transition-all shadow-md flex items-center gap-1.5 ${
                isFormComplete
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/30 hover:scale-[1.02] active:scale-95 cursor-pointer"
                  : "bg-gray-200 text-gray-400 border border-gray-300/60 cursor-not-allowed shadow-none"
              }`}
            >
              {isFormComplete && <span className="font-black text-sm">✓</span>}
              <span>{isFormComplete ? "Confirm Appointment" : "Fill Required Info"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewAppointmentModal;
